import { createRouter, createRootRoute, createRoute, redirect } from '@tanstack/react-router';
import { App } from './App';
import { IndexRoute } from './routes/index';
import { LoginPage } from './routes/login';
import { AuthCallbackPage } from './routes/auth/callback';
import { ChatPage } from './routes/chat';
import { queryClient } from './lib/query';
import type { AuthUser } from './lib/auth';

const rootRoute = createRootRoute({
  component: App,
});

// Helper to check auth status
async function checkAuth(): Promise<AuthUser | null> {
  const cached = queryClient.getQueryData<AuthUser | null>(['auth', 'user']);
  if (cached !== undefined) {
    return cached;
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/auth/me`,
      { credentials: 'include' }
    );
    if (response.ok) {
      const data = await response.json();
      queryClient.setQueryData(['auth', 'user'], data.user);
      return data.user;
    }
  } catch {
    // Auth check failed
  }
  queryClient.setQueryData(['auth', 'user'], null);
  return null;
}

// Protected route - redirects to /login if not authenticated
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IndexRoute,
  beforeLoad: async () => {
    const user = await checkAuth();
    if (!user) {
      throw redirect({ to: '/login', search: {} });
    }
  },
});

// Public route - login page
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { error?: string } => ({
    error: search.error as string | undefined,
  }),
  beforeLoad: async () => {
    const user = await checkAuth();
    if (user) {
      throw redirect({ to: '/' });
    }
  },
});

// Protected route - agent chat test page
const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat',
  component: ChatPage,
  validateSearch: (search: Record<string, unknown>): { session?: string } => ({
    session: search.session as string | undefined,
  }),
  beforeLoad: async () => {
    const user = await checkAuth();
    if (!user) {
      throw redirect({ to: '/login', search: {} });
    }
  },
});

// Public route - auth callback
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackPage,
});

const routeTree = rootRoute.addChildren([indexRoute, loginRoute, chatRoute, authCallbackRoute]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
